<!--
  File name: Filters.vue
  Program Description:
    - This component allows you to filter the PDBNF data.
    - The filtered data are used for the PDBNF Reports
    generation page.
-->
<template>
  <v-card>
    <v-card-title>
      Filters
    </v-card-title>
    <v-row class="pl-4 pr-4">
      <v-col class="pb-0">
        <v-select
          v-model="timeFrame"
          :items="['Monthly', 'Quarterly', 'Yearly']"
          label="Time Frame"
          dense
          outlined
          clearable
        />
      </v-col>
    </v-row>
    <v-row
      class="pl-4 pr-4"
    >
      <v-col
        class="pt-0"
      >
        <v-select
          v-if="timeFrame"
          v-model="year"
          :items="yearItems"
          label="Year"
          dense
          outlined
          multiple
        />
      </v-col>
      <v-col
        v-if="timeFrame === 'Quarterly'"
        class="pt-0"
      >
        <v-select
          v-model="quarter"
          :items="['1st Qtr', '2nd Qtr', '3rd Qtr', '4th Qtr']"
          label="Quarter"
          dense
          outlined
          multiple
        />
      </v-col>
      <v-col
        v-if="timeFrame === 'Monthly'"
        class="pt-0"
      >
        <v-select
          v-model="month"
          :items="monthItems"
          label="Month"
          dense
          outlined
          multiple
        />
      </v-col>
    </v-row>
    <template v-if="!isExternal">
      <v-card-text class="pt-0">
        Sector
      </v-card-text>
      <v-row class="pl-4 pr-4">
        <v-col class="pb-0">
          <VAutocomplete
            v-model="sector"
            label="Sector"
            text-field-class="black--text required-field font-weight-medium pt-0 text-h6"
            clearable
            dense
            :items="sectorItems"
            item-text="value"
            item-value="_id"
            outlined
            :append-icon="'arrow_drop_down'"
            multiple
          />
        </v-col>
      </v-row>
      <v-row class="pl-4 pr-4">
        <v-col class="pt-0">
          <VAutocomplete
            v-model.trim="subsector"
            label="Subsector"
            text-field-class="black--text required-field font-weight-medium pt-0 text-h6"
            clearable
            dense
            :disabled="!sector.length"
            :items="subsectorItems"
            item-text="value"
            item-value="_id"
            outlined
            :append-icon="'arrow_drop_down'"
            multiple
          />
        </v-col>
      </v-row>
    </template>
    <v-card-text class="pt-0">
      Cause
    </v-card-text>
    <v-row class="pl-4 pr-4">
      <v-col class="pb-0">
        <VAutocomplete
          v-model="generalCause"
          label="General Cause"
          text-field-class="black--text required-field font-weight-medium pt-0 text-h6"
          clearable
          dense
          :items="causeItems"
          item-text="value"
          item-value="_id"
          outlined
          :append-icon="'arrow_drop_down'"
          multiple
        />
      </v-col>
    </v-row>
    <v-row class="pl-4 pr-4">
      <v-col class="pt-0">
        <VAutocomplete
          v-model="specificCause"
          label="Specific Cause"
          text-field-class="black--text required-field font-weight-medium pt-0 text-h6"
          clearable
          dense
          :disabled="!generalCause.length"
          :items="specCauseItems"
          item-text="value"
          item-value="_id"
          outlined
          :append-icon="'arrow_drop_down'"
          multiple
        />
      </v-col>
    </v-row>
    <v-card-text class="pt-0 pb-2">
      Record Type
    </v-card-text>
    <v-row
      class="pl-4 pr-4"
    >
      <v-col>
        <LookUpSelect
          v-model="recordType"
          class="look-up-select"
          label="Record Type"
          name="recordType"
          text-field-class="black--text required-field font-weight-medium pt-0 text-h6"
          :query="{}"
          :allow-fetch="true"
          :can-edit="true"
          :clearable="true"
          :is-dense="true"
          outlined
          :append-icon="'arrow_drop_down'"
          item-text="value"
          item-value="_id"
          multiple
        />
      </v-col>
    </v-row>
    <template v-if="!isExternal">
      <v-card-text class="pt-0 pb-0">
        Notification Type
      </v-card-text>
      <v-row class="pl-4 pr-4">
        <v-col>
          <v-checkbox
            v-model="notification"
            class="pl-4"
            label="Voluntary"
            value="Voluntary"
          />
        </v-col>
        <v-col>
          <v-checkbox
            v-model="notification"
            label="Mandatory"
            value="Mandatory"
          />
        </v-col>
      </v-row>
      <v-card-text class="pt-0 pb-0">
        Classification
      </v-card-text>
      <v-row class="pl-4 pr-4">
        <v-col>
          <v-checkbox
            v-model="classification"
            class="pl-4"
            label="Government"
            value="government"
          />
        </v-col>
        <v-col>
          <v-checkbox
            v-model="classification"
            label="Private"
            value="private"
          />
        </v-col>
      </v-row>
      <v-card-text class="pt-0 pb-2">
        Status
      </v-card-text>
      <v-row class="pl-4 pr-4">
        <v-col>
          <v-select
            v-model="dbnStatus"
            label="Status"
            text-field-class="black--text required-field font-weight-medium pt-0 text-h6"
            clearable
            outlined
            :items="statusList"
            chips
            item-text="value"
            item-value="_id"
            multiple
          >
            <template #selection="{ item }">
              <StatusChip :status="item.value" />
            </template>
            <template #item="{ item }">
              <v-checkbox
                v-model="dbnStatus"
                class="mr-2"
                :value="item._id"
                @click="clickStatusCheckbox(item._id)"
              />
              <StatusChip :status="item.value" />
            </template>
          </v-select>
        </v-col>
      </v-row>
      <v-card-text class="pt-0 pb-2">
        Document Type
      </v-card-text>
      <v-row class="pl-4 pr-4">
        <v-col>
          <v-select
            v-model="documentType"
            :items="['PDBN', 'PBNEF', 'FBNER']"
            label="Document Type"
            dense
            multiple
            outlined
            clearable
          />
        </v-col>
      </v-row>
    </template>
  </v-card>
</template>

<script>
import { mapFields } from 'vuex-map-fields'
import StatusChip from '@/components/StatusChip'

export default ({
  name: 'Filters',
  components: { StatusChip },
  data: () => ({
  }),
  computed: {
    ...mapFields('pdbnfGraph', [
      'timeFrame',
      'year',
      'month',
      'quarter',
      'sector',
      'subsector',
      'generalCause',
      'specificCause',
      'recordType',
      'notification',
      'classification',
      'dbnStatus',
      'sectorList',
      'causeList',
      'recordList',
      'yearItems',
      'statusList',
      'documentType'
    ]),
    isExternal () {
      return this.$s.userType === 'External'
    },
    monthItems () {
      const year = new Date().getFullYear()
      if ((this.year && this.year[0] === year.toString()) && this.year.length === 1) {
        return this.currentYearMonths
      } else {
        return [
          'January',
          'February',
          'March',
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December'
        ]
      }
    },
    currentYearMonths () {
      const month = new Date().getMonth()
      const months = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December']
      let items

      months.forEach((i, idx) => {
        if (idx === month) {
          items = months.slice(0, idx + 1)
        }
      })
      return items
    },
    sectorItems () {
      const sectors = []
      this.sectorList.forEach((sec) => {
        if (sec.type === 'sector') sectors.push(sec)
      })
      return sectors
    },
    subsectorItems () {
      const subsectors = []
      this.sectorList.forEach((sec) => {
        if (sec.type === 'subsector') {
          this.sector.forEach((item) => {
            if (sec.jurisdiction === item) subsectors.push(sec)
          })
        }
      })
      return subsectors
    },
    causeItems () {
      const causes = []
      this.causeList.forEach((cause) => {
        if (cause.type === 'general') causes.push(cause)
      })
      return causes
    },
    specCauseItems () {
      const specCauses = []
      this.causeList.forEach((cause) => {
        if (cause.type === 'specific') {
          this.generalCause.forEach((item) => {
            if (cause.jurisdiction === item) specCauses.push(cause)
          })
        }
      })
      return [...specCauses, 'Others']
    }
  },
  methods: {
    // Add or remove dbnStatus value
    clickStatusCheckbox (id) {
      if (!this.dbnStatus.includes(id)) {
        this.dbnStatus.push(id)
      } else {
        const idx = this.dbnStatus.indexOf(id)
        this.dbnStatus.splice(idx, 1)
      }
    }
  }

})
</script>
